<template>
  <div>
    <h3 class="title mb-4">Review Tenancy Details</h3>
    <p class="text-subtitle-1 mb-6">Please review all information before submitting</p>
    
    <!-- Property Section -->
    <v-card class="mb-4 rounded-md" outlined>
      <v-card-title class="secondary white--text pa-3">
        <v-icon class="mr-2" color="white" small>mdi-home</v-icon>
        <span class="subtitle-2 white--text font-weight-light">Property Details</span>
      </v-card-title>
      <v-card-text>
        <div v-if="formData.property.isExisting && formData.property.existingProperty">
          <div class="text-subtitle-2">Existing Property</div>
          <div>{{ formData.property.existingProperty.Subject }}</div>
          <div class="text-caption">ID: {{ formData.property.existingProperty.OID }}</div>
        </div>
        <div v-else-if="formData.property.isExisting && !formData.property.existingProperty">
          <div class="text-subtitle-2">Existing Property</div>
          <div class="text-caption text--secondary">No property selected</div>
        </div>
        <div v-else-if="!formData.property.isExisting">
          <div class="text-subtitle-2">New Property</div>
          <div>{{ formatPropertyAddress(formData.property.newProperty) }}</div>
          <div class="text-caption">Type: {{ getPropertyTypeName(formData.property.newProperty.type) }}</div>
        </div>

        <!-- Tenancy Management Fee Override (if enabled) -->
        <v-divider
          v-if="formData.propertyManagementFee && formData.propertyManagementFee.overrideEnabled"
          class="my-3"
        ></v-divider>

        <div
          v-if="formData.propertyManagementFee && formData.propertyManagementFee.overrideEnabled"
          class="mt-2"
        >
          <div class="text-subtitle-2 mb-2">
            <v-icon small color="primary" class="mr-1">mdi-percent</v-icon>
            Tenancy Management Fee Override
          </div>
          <v-row>
            <v-col cols="6" md="3">
              <div class="text-caption grey--text">Fee Percentage</div>
              <div class="font-weight-medium">
                {{ formData.propertyManagementFee.managementFeePercent }}%
              </div>
            </v-col>
            <v-col cols="6" md="3">
              <div class="text-caption grey--text">Effective Date</div>
              <div class="font-weight-medium">
                {{ formatDate(formData.propertyManagementFee.dateManagementFee) }}
              </div>
            </v-col>
          </v-row>

          <!-- Warning badge if landlord fee also set -->
          <v-alert
            v-if="hasLandlordFee"
            type="info"
            dense
            text
            class="mt-2"
          >
            <small>This tenancy management fee overrides the landlord's management fee for this tenancy only</small>
          </v-alert>
        </div>
      </v-card-text>
    </v-card>
    
    <!-- Landlord Section -->
    <v-card class="mb-4 rounded-md" outlined>
      <v-card-title class="secondary white--text pa-3">
        <v-icon class="mr-2" color="white" small>mdi-account-tie</v-icon>
        <span class="subtitle-2 white--text font-weight-light">Landlord Details</span>
      </v-card-title>
      <v-card-text>
        <div v-if="formData.landlord.isExisting && formData.landlord.existingLandlord">
          <div class="text-subtitle-2">Existing Landlord</div>
          <div>{{ formData.landlord.existingLandlord.Subject }}</div>
          <div class="text-caption">ID: {{ formData.landlord.existingLandlord.OID }}</div>
        </div>
        <div v-else-if="formData.landlord.isExisting && !formData.landlord.existingLandlord">
          <div class="text-subtitle-2">Existing Landlord</div>
          <div class="text-caption text--secondary">No landlord selected</div>
        </div>
        <div v-else-if="!formData.landlord.isExisting">
          <div class="text-subtitle-2">New Landlord</div>
          <div>{{ formatPersonName(formData.landlord.newLandlord) }}</div>
          <div class="text-caption">{{ formData.landlord.newLandlord.contactEmail }}</div>
          <div class="text-caption">{{ formData.landlord.newLandlord.mobilePhone }}</div>

          <!-- Landlord Management Fee (if provided) -->
          <v-divider
            v-if="hasLandlordFee"
            class="my-3"
          ></v-divider>

          <div v-if="hasLandlordFee" class="mt-2">
            <div class="text-subtitle-2 mb-2">
              <v-icon small color="primary" class="mr-1">mdi-percent</v-icon>
              Landlord Management Fee
            </div>
            <v-row>
              <v-col cols="6" md="3">
                <div class="text-caption grey--text">Fee Percentage</div>
                <div class="font-weight-medium">
                  {{ formData.landlord.newLandlord.managementFeePercent }}%
                </div>
              </v-col>
              <v-col cols="6" md="3">
                <div class="text-caption grey--text">Effective Date</div>
                <div class="font-weight-medium">
                  {{ formatDate(formData.landlord.newLandlord.dateManagementFee) }}
                </div>
              </v-col>
            </v-row>
          </div>
        </div>
      </v-card-text>
    </v-card>
    
    <!-- Area/Branch Section -->
    <v-card class="mb-4 rounded-md" outlined>
      <v-card-title class="secondary white--text pa-3">
        <v-icon class="mr-2" color="white" small>mdi-map-marker</v-icon>
        <span class="subtitle-2 white--text font-weight-light">Area/Branch Details</span>
      </v-card-title>
      <v-card-text>
        <div v-if="formData.area.isExisting && formData.area.existingArea">
          <div class="text-subtitle-2">Existing Area</div>
          <div>{{ formData.area.existingArea.Subject || formData.area.existingArea.Name }}</div>
          <div class="text-caption">ID: {{ formData.area.existingArea.OID }}</div>
        </div>
        <div v-else-if="formData.area.isExisting && !formData.area.existingArea">
          <div class="text-subtitle-2">Existing Area</div>
          <div class="text-caption text--secondary">No area selected</div>
        </div>
        <div v-else-if="!formData.area.isExisting">
          <div class="text-subtitle-2">New Area</div>
          <div>{{ formData.area.newArea.areaName }}</div>
          <div class="text-caption">Branch: {{ getBranchName(formData.area.newArea.branchId) }}</div>
        </div>
      </v-card-text>
    </v-card>
    
    <!-- Tenancy Details Section -->
    <v-card class="mb-4 rounded-md" outlined>
      <v-card-title class="secondary white--text pa-3">
        <v-icon class="mr-2" color="white" small>mdi-file-document</v-icon>
        <span class="subtitle-2 white--text font-weight-light">Tenancy Details</span>
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="6" md="3">
            <div class="text-caption">Stage</div>
            <div>{{ getTenancyStageName(formData.tenancy.stage) }}</div>
          </v-col>
          <v-col cols="6" md="3">
            <div class="text-caption">Service Type</div>
            <div>{{ getServiceTypeName(formData.tenancy.serviceTypeId) }}</div>
          </v-col>
          <v-col cols="6" md="3">
            <div class="text-caption">Rent</div>
            <div>£{{ formData.tenancy.advertisedRent }}</div>
          </v-col>
          <v-col cols="6" md="3">
            <div class="text-caption">Deposit</div>
            <div>£{{ formData.tenancy.deposit }}</div>
          </v-col>
        </v-row>
        <v-row v-if="formData.tenancy.startDate" class="mt-2">
          <v-col cols="6" md="3">
            <div class="text-caption">Start Date</div>
            <div>{{ formatDate(formData.tenancy.startDate) }}</div>
          </v-col>
          <v-col cols="6" md="3">
            <div class="text-caption">Fixed Term End</div>
            <div>{{ formatDate(formData.tenancy.fixedDate) }}</div>
          </v-col>
          <v-col cols="6" md="3">
            <div class="text-caption">End Date</div>
            <div>{{ formatDate(formData.tenancy.endDate) || 'Not specified' }}</div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
    
    <!-- Tenants Section -->
    <v-card class="mb-4 rounded-md" outlined>
      <v-card-title class="secondary white--text pa-3">
        <v-icon class="mr-2" color="white" small>mdi-account-group</v-icon>
        <span class="subtitle-2 white--text font-weight-light">Tenants ({{ formData.tenants.length }})</span>
      </v-card-title>
      <v-card-text>
        <div
          v-for="(tenant, index) in formData.tenants"
          :key="index"
          class="mb-3"
        >
          <div class="d-flex align-center">
            <div class="flex-grow-1">
              <div v-if="tenant.isExisting && tenant.existingTenant">
                <div class="text-subtitle-2">{{ tenant.existingTenant.Subject }}</div>
                <div class="text-caption">ID: {{ tenant.existingTenant.OID }}</div>
              </div>
              <div v-else-if="tenant.isExisting && !tenant.existingTenant">
                <div class="text-subtitle-2">Existing Tenant</div>
                <div class="text-caption text--secondary">No tenant selected</div>
              </div>
              <div v-else-if="!tenant.isExisting && tenant.newTenant">
                <div class="text-subtitle-2">{{ formatPersonName(tenant.newTenant) }}</div>
                <div class="text-caption">{{ tenant.newTenant.emailAddress }}</div>
              </div>
            </div>
            <v-chip
              v-if="index === 0 || (tenant.newTenant && tenant.newTenant.isHeadTenant)"
              color="primary"
              small
            >
              Head Tenant
            </v-chip>
          </div>
        </div>
      </v-card-text>
    </v-card>
    
    <!-- Confirmation Checkbox -->
    <v-row class="mt-4">
      <v-col cols="12">
        <v-checkbox
          v-model="confirmed"
          color="primary"
          class="mt-0"
        >
          <template v-slot:label>
            <span class="body-2">
              I confirm that the tenancy details I have provided are correct
            </span>
          </template>
        </v-checkbox>
      </v-col>
    </v-row>

    <!-- Navigation buttons -->
    <v-row class="mt-2">
      <v-col cols="6">
        <v-btn
          text
          @click="$emit('back')"
          :disabled="loading"
        >
          <v-icon left>mdi-arrow-left</v-icon>
          Back
        </v-btn>
      </v-col>
      <v-col cols="6" class="text-right">
        <v-btn
          color="primary"
          :loading="loading"
          :disabled="!confirmed"
          @click="$emit('submit')"
        >
          Create Tenancy
          <v-icon right>mdi-check</v-icon>
        </v-btn>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'

export default {
  name: 'ReviewStep',

  props: {
    formData: {
      type: Object,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      confirmed: false
    }
  },

  computed: {
    ...mapGetters('TenanciesManagement', ['serviceTypeOptions']),
    ...mapState(['allBranches']),

    hasLandlordFee() {
      return this.formData.landlord &&
             !this.formData.landlord.isExisting &&
             this.formData.landlord.newLandlord &&
             this.formData.landlord.newLandlord.managementFeePercent !== null &&
             this.formData.landlord.newLandlord.managementFeePercent !== ''
    }
  },
  
  methods: {
    formatPropertyAddress(property) {
      const parts = [
        property.addressNumber,
        property.address1,
        property.address2,
        property.address3,
        property.address4,
        property.postcode
      ].filter(Boolean)
      
      return parts.join(', ')
    },
    
    formatPersonName(person) {
      const parts = [
        person.title,
        person.forename,
        person.middleName,
        person.surname
      ].filter(Boolean)
      
      return parts.join(' ')
    },
    
    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('en-GB')
    },
    
    getPropertyTypeName(type) {
      const types = {
        0: 'House',
        1: 'Flat',
        2: 'Bungalow',
        3: 'Studio',
        4: 'Other'
      }
      return types[type] || 'Unknown'
    },
    
    getTenancyStageName(stage) {
      const stages = {
        0: 'New Instruction',
        1: 'Advertised',
        2: 'Let Agreed',
        3: 'Signed'
      }
      return stages[stage] || 'Unknown'
    },
    
    getServiceTypeName(serviceTypeId) {
      const serviceType = this.serviceTypeOptions.find(st => st.value === serviceTypeId)
      return serviceType ? serviceType.text : 'Unknown'
    },
    
    getBranchName(branchId) {
      const branch = this.allBranches.find(b => b.OID === branchId)
      return branch ? branch.Name : 'Unknown'
    }
  }
}
</script>